import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { selectUserToken } from "../../../../../features/userSlice";
import { familiesServices } from "../../../../../services/api/families.endpoints";
import { familiesEndPointLogic } from "../../../../../logic/DataCollection/Families/FamiliesEndPointLogic";
import EditFamilyModalSkeleton from "./EditFamilyModalSkeleton";
import ConfigAndMapDetailsModal from "../../ByInterface/InterfaceView/ConfigAndMapDetailsModal/ConfigAndMapDetailsModal";
import ConnectabilityConfirmModal from "../../shared/ConnectabilityConfirmModal";
import type { FamilyItem } from "./NeedAttentionFamiliesAction";
import { useWebsiteSharedUid } from "../../../../../customHooks/useWebsiteSharedUid";
import {
  buildCandidatesFromCoverageRow,
  buildCandidatesFromThings,
  enrichCandidates,
  formatCandidateLabel,
  type CandidateItem,
  type CoverageRow,
} from "../../shared/candidateCatalogue";
import {
  EDIT_FAMILY_DETACH_TRACKER,
  EDIT_FAMILY_ATTACH_TRACKER,
  EDIT_FAMILY_SAVE_TRACKER,
  EDIT_FAMILY_CANCEL_TRACKER,
} from "../../shared/familyActionTrackers";

interface Props {
  open: boolean;
  handleClose: () => void;
  item: FamilyItem | null;
  onSaved?: () => void;
}

const isDestinationCovered = (item: CandidateItem, stagedItems: { destination_table?: string | null }[]) =>
  !!item.destination_table &&
  stagedItems.some((s) => s.destination_table === item.destination_table);

function thingId(thing: any) {
  return thing.config_id ?? thing.map_id;
}

function parseJsonField(value: any) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
}

function recipeLine(thing: any) {
  return [
    thing.report_name,
    thing.collection_method,
    thing.collection_type,
    thing.format,
    thing.sub_filtering_method,
  ]
    .filter(Boolean)
    .join(" - ");
}

const EditFamilyModal = ({ open, handleClose, item, onSaved }: Props) => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  // Families data spans all interfaces/brands, not scoped to one website —
  // the EP treats website_shared_uid as a required dummy input here, so we
  // pass the first website_shared_uid the user can access from /me.
  const website_shared_uid = useWebsiteSharedUid();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [family, setFamily] = useState<any>(null);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [stagedMapIds, setStagedMapIds] = useState<(string | number)[]>([]);
  const [selectedThing, setSelectedThing] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [replacingId, setReplacingId] = useState<string | number | null>(null);
  // Q1 things for this interface+step — only used to enrich Q2 candidates
  // with destination_table / subtitle, never as a source of options.
  const [q1Candidates, setQ1Candidates] = useState<CandidateItem[]>([]);
  const { interfaceCoverage, isInterfaceCoverageLoading } = useSelector(
    (state: any) => state.families,
  );
  const [pendingConfirm, setPendingConfirm] = useState<{
    mapIds: (string | number)[];
    message?: string;
  } | null>(null);

  useEffect(() => {
    if (!open || !item) return;

    let cancelled = false;

    const fetchFamily = async () => {
      setIsLoading(true);
      setFamily(null);
      try {
        const response = await familiesServices.getFamiliesOverview(
          oauthTokenHeader,
          website_shared_uid,
          item.backendInterfaceId,
        );
        const rows = response?.data?.data?.query_output ?? [];
        const match = rows.find(
          (row: any) => row.family_id === item.id && row.step === item.step,
        );
        if (!cancelled && match) {
          const parsed = {
            ...match,
            brands: parseJsonField(match.brands),
            things: parseJsonField(match.things),
          };
          setFamily(parsed);
          setName(match.ui_family_name ?? "");
          setNameError("");
          setStagedMapIds(parsed.things.map(thingId));
        }

        const thingsForStep = rows
          .filter((row: any) => row.step === item.step)
          .flatMap((row: any) => parseJsonField(row.things));
        if (!cancelled) setQ1Candidates(buildCandidatesFromThings(thingsForStep));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchFamily();
    setSearch("");
    setReplacingId(null);
    return () => {
      cancelled = true;
    };
  }, [open, item, oauthTokenHeader, website_shared_uid]);

  // This modal opens from both the Interface view and Needs Attention; the
  // latter doesn't load Q2 itself, so fetch it here if the store is empty.
  useEffect(() => {
    if (!open || interfaceCoverage.length || isInterfaceCoverageLoading) return;
    familiesEndPointLogic.getInterfaceCoverage(oauthTokenHeader, website_shared_uid, dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The option catalogue is the Q2 column for this interface+step (see
  // candidateCatalogue.ts), same as the Create drawer.
  const candidates = useMemo(() => {
    const coverageRow: CoverageRow | undefined = interfaceCoverage.find(
      (row: any) => String(row.backend_interface_id) === String(item?.backendInterfaceId),
    );
    return enrichCandidates(
      buildCandidatesFromCoverageRow(coverageRow, item?.step),
      q1Candidates,
    );
  }, [interfaceCoverage, item?.backendInterfaceId, item?.step, q1Candidates]);

  const originalMapIds = useMemo(
    () => (family?.things ?? []).map(thingId),
    [family],
  );

  // Staged rows can come from two different shapes: the family's real,
  // already-attached things (full metadata from Q1), or a newly-picked
  // catalogue candidate (name/count/description, and for Unifier/Advisor a
  // real destination_table) — normalize both for rendering.
  const stagedThings = useMemo(
    () =>
      stagedMapIds
        .map((id) => {
          const real = (family?.things ?? []).find(
            (t: any) => String(thingId(t)) === String(id),
          );
          if (real) {
            return {
              id,
              name: real.configuration_name ?? real.map_name,
              count: id,
              subtitle: real.destination_table ?? recipeLine(real),
              destination_table: real.destination_table ?? null,
              raw: real,
            };
          }
          const candidate = candidates.find((c) => String(c.id) === String(id));
          if (candidate) {
            return {
              id,
              name: candidate.name,
              count: candidate.id,
              subtitle: candidate.description,
              destination_table: candidate.destination_table ?? null,
              raw: null,
            };
          }
          return null;
        })
        .filter((x): x is NonNullable<typeof x> => !!x),
    [stagedMapIds, family, candidates],
  );

  const isFetcher = item?.step === "fetcher";
  const remaining = candidates.filter(
    (c) =>
      !stagedMapIds.some((id) => String(id) === String(c.id)) &&
      formatCandidateLabel(c).toLowerCase().includes(search.toLowerCase()) &&
      (isFetcher || !isDestinationCovered(c, stagedThings)),
  );

  const stageCandidate = (candidate: CandidateItem) => {
    if (replacingId) {
      setStagedMapIds((prev) =>
        prev.map((id) => (String(id) === String(replacingId) ? candidate.id : id)),
      );
      setReplacingId(null);
    } else {
      setStagedMapIds((prev) => (prev.includes(candidate.id) ? prev : [...prev, candidate.id]));
    }
    setSearch("");
  };

  const handleSwitchClick = (id: string | number) => {
    setReplacingId(id);
    setSearch("");
  };

  const handleDetachClick = (id: string | number) => {
    setStagedMapIds((prev) => prev.filter((x) => String(x) !== String(id)));
    if (String(replacingId) === String(id)) setReplacingId(null);
  };

  // The real API expects the full { ui_family_name, map_ids } shape on every
  // PUT, not just the fields that changed — confirmed against a real
  // Postman request. "hasChanges" below still gates the Save button/no-op
  // close purely for UX, independent of what's actually sent.
  const buildSavePayload = () => ({
    ui_family_name: name.trim(),
    map_ids: stagedMapIds,
  });

  const nameChanged = !!family && name.trim() !== (family.ui_family_name ?? "");
  const changedMapIds =
    stagedMapIds.length !== originalMapIds.length ||
    stagedMapIds.some(
      (id) => !originalMapIds.some((o: any) => String(o) === String(id)),
    );
  const hasChanges = nameChanged || changedMapIds;

  const removedExistingIds = originalMapIds.filter(
    (id: any) => !stagedMapIds.some((s) => String(s) === String(id)),
  );

  const pendingPayload = buildSavePayload();

  const commitSave = async (payload: any) => {
    if (!family) return;
    setIsSaving(true);
    // Q1 now returns each family's own shared_uid (family_shared_uid) —
    // that's the real URL id for PUT/DELETE, replacing the earlier
    // website_shared_uid placeholder.
    const success = await familiesEndPointLogic.updateFamily(
      oauthTokenHeader,
      family.family_shared_uid,
      { step: family.step, backendInterfaceId: family.backend_interface_id },
      payload,
      dispatch,
    );
    setIsSaving(false);
    if (success) {
      onSaved?.();
      handleClose();
    }
  };

  const handleSave = async () => {
    // The real API requires a non-empty ui_family_name — blanking the field
    // and saving would 400 with "ui_family_name is required".
    if (!name.trim()) {
      setNameError("Family name is required.");
      return;
    }
    setNameError("");

    if (!hasChanges) {
      handleClose();
      return;
    }

    if (removedExistingIds.length > 0 && (family?.brands_count ?? 0) > 0) {
      setIsSaving(true);
      const { risky, message } = await familiesEndPointLogic.checkFamiliesConnectability(
        oauthTokenHeader,
        {
          family_id: family.family_id,
          backend_interface_id: family.backend_interface_id,
          map_ids: pendingPayload.map_ids,
        },
      );
      setIsSaving(false);
      if (risky) {
        setPendingConfirm({ mapIds: pendingPayload.map_ids, message });
        return;
      }
    }

    await commitSave(pendingPayload);
  };

  const handleConfirmRisky = async () => {
    if (!pendingConfirm) return;
    const payload = buildSavePayload();
    setPendingConfirm(null);
    await commitSave(payload);
  };

  if (!item) return null;

  const stepTitle = item.step
    ? item.step.charAt(0).toUpperCase() + item.step.slice(1)
    : "";
  const thingsLabel = item.step === "fetcher" ? "Configs" : "Maps";
  const detachTracker = EDIT_FAMILY_DETACH_TRACKER[item.step];
  const attachTracker = EDIT_FAMILY_ATTACH_TRACKER[item.step];
  const saveTracker = EDIT_FAMILY_SAVE_TRACKER[item.step];
  const cancelTracker = EDIT_FAMILY_CANCEL_TRACKER[item.step];
  const replacingItem = replacingId
    ? stagedThings.find((t) => String(t.id) === String(replacingId))
    : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{ zIndex: 1300 }}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        handleClose();
      }}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
    >
      <Box sx={{ p: 3, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
            Edit {stepTitle} Family
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {isLoading ? (
          <EditFamilyModalSkeleton />
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 1.5 }}>
                Family Details
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#00000099" }}>Name*</Typography>
              <TextField
                fullWidth
                variant="standard"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError("");
                }}
                color="secondary"
                error={!!nameError}
              />
              <Typography sx={{ fontSize: 12, color: nameError ? "#D32F2F" : "#00000061", mt: 0.5 }}>
                {nameError || "This field is mandatory"}
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 1.5 }}>
                {thingsLabel}
              </Typography>

              {replacingId && (
                <Typography sx={{ fontSize: 12, color: "#9C27B0", mb: 0.5 }}>
                  Switching &quot;{replacingItem?.name ?? "item"}&quot; — pick a replacement below.{" "}
                  <Box
                    component="span"
                    onClick={() => setReplacingId(null)}
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    Cancel
                  </Box>
                </Typography>
              )}

              <Autocomplete
                size="small"
                options={remaining}
                inputValue={search}
                onInputChange={(_, val) => setSearch(val)}
                onChange={(_, val) => {
                  if (val) stageCandidate(val);
                }}
                getOptionLabel={formatCandidateLabel}
                filterOptions={(opts) => opts}
                noOptionsText={`No available ${thingsLabel.toLowerCase()}.`}
                renderOption={(props, c) => (
                  <Box component="li" {...props} key={c.id} data-tracker={attachTracker}>
                    <Box>
                      <Typography sx={{ color: "#9C27B0", fontSize: 14, fontWeight: 500 }}>
                        {c.name}{" "}
                        <Box component="span" sx={{ color: "#00000061", fontSize: 12, fontWeight: 500 }}>
                          ({c.id})
                        </Box>
                      </Typography>
                      <Typography sx={{ color: "#00000099", fontSize: 12 }}>
                        {c.description}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={`Search ${thingsLabel}`}
                    color="secondary"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ fontSize: 20, color: "#666", mr: 0.5 }} />,
                    }}
                  />
                )}
              />
              <Typography sx={{ fontSize: 12, color: "#00000061", mt: 0.5 }}>
                You can select {thingsLabel.toLowerCase()} now or later.
              </Typography>

              {stagedThings.length === 0 ? (
                <Box sx={{ border: "1px dashed #BDBDBD", borderRadius: "4px", p: 1, textAlign: "center", mt: 1.5 }}>
                  <Typography sx={{ fontSize: 12, color: "#00000099" }}>
                    No {thingsLabel.toLowerCase()} attached.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  {stagedThings.map((thing) => (
                    <Box
                      key={thing.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        border: "1px solid #0000001F",
                        borderRadius: "8px",
                        p: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          onClick={thing.raw ? () => setSelectedThing(thing.raw) : undefined}
                          sx={{
                            color: "#9C27B0",
                            fontSize: 14,
                            fontWeight: 500,
                            cursor: thing.raw ? "pointer" : "default",
                          }}
                        >
                          {thing.name}{" "}
                          <Box component="span" sx={{ color: "#00000061", fontSize: 12, fontWeight: 500 }}>
                            ({thing.count})
                          </Box>
                        </Typography>
                        <Typography sx={{ color: "#00000099", fontSize: 12 }}>
                          {thing.subtitle}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} flexShrink={0}>
                        <IconButton size="small" onClick={() => handleSwitchClick(thing.id)} sx={{ color: "#9C27B0" }}>
                          <SwapHorizIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDetachClick(thing.id)}
                          data-tracker={detachTracker}
                          sx={{ color: "#9C27B0" }}
                        >
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </>
        )}

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
          <Button variant="text" color="secondary" onClick={handleClose} data-tracker={cancelTracker}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSave}
            disabled={isLoading || isSaving || !hasChanges}
            startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
            data-tracker={saveTracker}
          >
            Save Edits
          </Button>
        </Stack>
      </Box>

      {selectedThing && (
        <ConfigAndMapDetailsModal
          open={!!selectedThing}
          handleClose={() => setSelectedThing(null)}
          stepKey={item.step}
          interfaceName={item.provider}
          thing={selectedThing}
        />
      )}

      <ConnectabilityConfirmModal
        open={!!pendingConfirm}
        message={pendingConfirm?.message}
        isSubmitting={isSaving}
        handleCancel={() => setPendingConfirm(null)}
        handleConfirm={handleConfirmRisky}
      />
    </Drawer>
  );
};

export default EditFamilyModal;
